import React, {FC, Fragment} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {useAppDispatch} from '../../../helpers/appReduxHelpers';
import {SettingsRootPagesEnum} from '../../../models/url/models/types';
import {SettingsAdminHomeUrl} from '../../../models/url/settingsUrlModel';
import {useHomeShortcutClick} from '../../../settings/home/settingsHomeShortcutHook';
import {useOnboardingModalState} from '../../app/topBar/onboarding/onboardingContext';
import {OnboardingGuide} from '../onboardingGuide';
import {OnboardingGuideTask} from '../onboardingGuideTaskV2';
import {AdminOnboardingTaskGroupHeader} from './adminOnboardingTaskGroupHeader';
import {computeOnboardingProgress, OnboardingTask, OnboardingTaskGroup} from './tasks/onboardingTasks';

/*
 * Props.
 */

interface AdminOnboardingGuideProps {
  previousTaskGroups: ReadonlyArray<OnboardingTaskGroup>;
  taskGroups: ReadonlyArray<OnboardingTaskGroup>;
  onRequestClose: () => void;
}

/*
 * Component.
 */

export const AdminOnboardingGuide: FC<AdminOnboardingGuideProps> = (props) => {
  const {previousTaskGroups, taskGroups, onRequestClose} = props;
  const dispatch = useAppDispatch();
  const {setIsAdminWizardOpen} = useOnboardingModalState();

  const url: SettingsAdminHomeUrl = {rootPage: SettingsRootPagesEnum.ADMIN_HOME};
  const {onShortcutClick} = useHomeShortcutClick(url);

  const onTaskClick = async (task: OnboardingTask) => {
    const promise = task.startTask({
      dispatch,
      openAdminWizard: () => setIsAdminWizardOpen(true),
      openSettingsHomeShortcut: onShortcutClick,
    });

    trackInterfaceEvent(InterfaceEventsEnum.PO_GETTING_STARTED_CLICK, {
      ...task.computeTrackingEventData(),
      category: 'task',
    });

    await promise;
    onRequestClose();
  };

  // Render the title based on the current/live completion status.
  const progress = computeOnboardingProgress(taskGroups);
  const title = renderTitle(progress.numCompletedTasks, progress.numTotalTasks);

  // Render the tasks based on the lagging completion status, so we get an animation upon opening the setup guide.
  const isCompact = previousTaskGroups.length > 1;
  const previousProgress = computeOnboardingProgress(previousTaskGroups);
  const renderContent = () =>
    previousTaskGroups.map((taskGroup) => (
      <Fragment key={taskGroup.key}>
        {taskGroup.title && (
          <AdminOnboardingTaskGroupHeader>{taskGroup.title}</AdminOnboardingTaskGroupHeader>
        )}
        {taskGroup.tasks.map((task, index) => (
          <OnboardingGuideTask
            key={task.key}
            title={task.title}
            description={task.description}
            renderDetails={task.details ? () => task.details : undefined}
            isComplete={Boolean(task.isTaskComplete)}
            onClick={() => onTaskClick(task)}
            stepNum={index + 1}
            isCompact={isCompact}
          />
        ))}
      </Fragment>
    ));

  return (
    <OnboardingGuide
      title={title}
      numCompletedTasks={previousProgress.numCompletedTasks}
      numTotalTasks={previousProgress.numTotalTasks}
      hasCompletedAllTasks={previousProgress.numCompletedTasks === previousProgress.numTotalTasks}
      isCompact={isCompact}
    >
      {renderContent()}
    </OnboardingGuide>
  );
};

/*
 * Helpers.
 */

function renderTitle(numCompletedTasks: number, numTotalTasks: number) {
  const remainingSteps = numTotalTasks - numCompletedTasks;

  switch (remainingSteps) {
    case 0:
      return (
        <Trans id="p0-admin-onboarding-guide-title-0">
          🎊️ Congrats! You&rsquo;re ready to dive into Front!
        </Trans>
      );
    case 1:
      return <Trans id="p0-admin-onboarding-guide-title-1">🎉 Last step! You are almost there.</Trans>;
    case 2:
      return (
        <Trans id="p0-admin-onboarding-guide-title-2">👍 Well done! You&rsquo;re making good progress.</Trans>
      );
    case 3:
      return <Trans id="p0-admin-onboarding-guide-title-3">🙌 Great start! Keep up the great work.</Trans>;
    default:
      return (
        <Trans id="p0-admin-onboarding-guide-title-start">
          🥳 Welcome! Time to learn, setup, and launch Front.
        </Trans>
      );
  }
}
